import React, { useEffect, useMemo, useState } from 'react';
import {
  BusinessOutlined as BusinessIcon,
  AccountCircleOutlined as ProfileIcon,
  HistoryOutlined as HistoryIcon,
  FactCheckOutlined as AuditIcon,
  Inventory2Outlined as InventoryIcon,
  DirectionsCarOutlined as VehicleIcon,
  ViewKanbanOutlined as OnboardingIcon,
  MemoryOutlined as MemoryIcon,
  SupportAgentOutlined as SupportIcon,
  SettingsOutlined as SettingsIcon,
  PeopleAltOutlined as PeopleIcon,
  NotificationsNoneOutlined as NotificationsIcon,
  PolicyOutlined as PolicyIcon,
  SecurityOutlined as SecurityIcon,
  TuneOutlined as PreferencesIcon,
  BuildOutlined as OperationsIcon,
  ContactMailOutlined as ContactInquiryIcon,
  MailOutline as MailIcon,
  Groups2Outlined as ThirdPartyIcon,
  WarningAmberOutlined as AnomalyIcon,
  WorkspacesOutlined as PortfolioIcon,
  HandymanOutlined as TechnicianIcon,
  SensorDoorOutlined as BoomBarrierIcon,
  QueryStatsOutlined as AccuracyIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import AppShell from './AppShell';
import { logout } from '../app/store/authSlice';
import { resetContext } from '../app/store/contextSlice';
import { resetUiState } from '../app/store/uiSlice';
import { can, canAny } from '../shared/utils/rbac';
import { PERMISSIONS } from '../shared/constants/permissions';
import {
  canAccessThirdPartySection,
  formatRoleLabel,
  normalizeRole,
  PARKTEK_INTERNAL_ROLES,
  ROLES,
} from '../shared/constants/roles';
import SocietySelector from './SocietySelector';
import ScopeChip from '../shared/components/ScopeChip';
import GlobalSearch from '../shared/components/GlobalSearch';
import buildGlobalSearchProviders from './globalSearchProviders';
import { resolvePageScope } from '../shared/utils/scope';
import { storage } from '../shared/lib/storage';
import { settingsApi } from '../features/settings/api';

const NAV_ITEMS = [
  {
    label: 'Societies',
    path: '/societies',
    icon: <BusinessIcon />,
    permission: PERMISSIONS.SOCIETIES_VIEW,
    match: ['/societies'],
  },
  {
    label: 'Onboarding',
    path: '/onboarding',
    icon: <OnboardingIcon />,
    permission: PERMISSIONS.ONBOARDING_VIEW,
    roles: [ROLES.SUPER_ADMIN, ROLES.DEVELOPER, ROLES.ACCOUNT_MANAGER],
    match: ['/onboarding'],
  },
  {
    label: 'Users',
    path: '/users',
    icon: <PeopleIcon />,
    permission: PERMISSIONS.USERS_VIEW,
    match: ['/users'],
  },
  {
    label: 'Vehicles',
    path: '/vehicles',
    icon: <VehicleIcon />,
    permission: PERMISSIONS.USERS_VIEW,
    match: ['/vehicles'],
  },
  {
    label: 'Third Party',
    path: '/thirdparty',
    icon: <ThirdPartyIcon />,
    permission: PERMISSIONS.USERS_VIEW,
    match: ['/thirdparty'],
  },
  {
    label: 'Requests',
    path: '/requests',
    icon: <MailIcon />,
    permission: PERMISSIONS.REQUESTS_VIEW,
    match: ['/requests'],
  },
  {
    label: 'Tags Inventory',
    path: '/inventory',
    icon: <InventoryIcon />,
    permission: PERMISSIONS.INVENTORY_SOCIETY_VIEW,
  },
  {
    label: 'Tags Inventory Global',
    path: '/inventory/global',
    icon: <InventoryIcon />,
    permission: PERMISSIONS.INVENTORY_GLOBAL_VIEW,
    match: ['/inventory/global'],
  },
  {
    label: 'Logs',
    path: '/logs',
    icon: <HistoryIcon />,
    permission: PERMISSIONS.LOGS_VIEW,
  },
  {
    label: 'ANPR Visitor Logs',
    path: '/anpr-visitor-logs',
    icon: <HistoryIcon />,
    permission: PERMISSIONS.LOGS_VIEW,
  },
  {
    label: 'ALPR Accuracy',
    path: '/alpr-accuracy',
    icon: <AccuracyIcon />,
    permission: PERMISSIONS.LOGS_VIEW,
    roles: [ROLES.SUPER_ADMIN, ROLES.DEVELOPER],
    match: ['/alpr-accuracy'],
  },
  {
    label: 'Push Button Activity Logs',
    path: '/boom-barrier-logs',
    icon: <BoomBarrierIcon />,
    permission: PERMISSIONS.LOGS_VIEW,
    roles: [ROLES.SUPER_ADMIN, ROLES.DEVELOPER, ROLES.SOCIETY_ADMIN],
  },
  {
    label: 'Audit Logs',
    path: '/audit-logs',
    icon: <AuditIcon />,
    permission: PERMISSIONS.AUDIT_LOGS_VIEW,
    roles: [ROLES.SUPER_ADMIN, ROLES.DEVELOPER],
    match: ['/audit-logs'],
  },
  {
    label: 'Controllers',
    path: '/controllers',
    icon: <MemoryIcon />,
    permission: PERMISSIONS.CONTROLLERS_VIEW,
    roles: [
      ROLES.SUPER_ADMIN,
      ROLES.DEVELOPER,
      ROLES.ACCOUNT_MANAGER,
      ROLES.SOCIETY_ADMIN,
      ROLES.SOCIETY_MANAGER,
      ROLES.SOCIETY_GUARD,
      ROLES.TECHNICIAN,
      ROLES.INSTALLATION_MANAGER,
    ],
    match: ['/controllers'],
  },
  {
    label: 'Device Operations',
    path: '/fleet-health',
    icon: <OperationsIcon />,
    permission: PERMISSIONS.CONTROLLERS_VIEW,
    roles: [ROLES.SUPER_ADMIN, ROLES.DEVELOPER],
    match: ['/fleet-health'],
  },
  {
    label: 'Anomalies',
    path: '/anomalies',
    icon: <AnomalyIcon />,
    permission: PERMISSIONS.LOGS_VIEW,
    roles: PARKTEK_INTERNAL_ROLES,
    match: ['/anomalies'],
  },
  {
    label: 'Technician Tasks',
    path: '/technician-tasks',
    icon: <TechnicianIcon />,
    permission: PERMISSIONS.CONTROLLERS_VIEW,
    roles: [ROLES.SUPER_ADMIN, ROLES.DEVELOPER, ROLES.ACCOUNT_MANAGER],
    match: ['/technician-tasks'],
  },
  {
    label: 'My Portfolio',
    path: '/portfolio',
    icon: <PortfolioIcon />,
    permission: PERMISSIONS.ONBOARDING_VIEW,
    roles: [ROLES.SUPER_ADMIN, ROLES.DEVELOPER, ROLES.ACCOUNT_MANAGER],
    match: ['/portfolio'],
  },
  {
    label: 'Support',
    path: '/support',
    icon: <SupportIcon />,
    permission: PERMISSIONS.SUPPORT_VIEW,
  },
  {
    label: 'Contact Inquiries',
    path: '/contact-inquiries',
    icon: <ContactInquiryIcon />,
    permission: PERMISSIONS.CONTACT_INQUIRIES_VIEW,
    roles: [ROLES.SUPER_ADMIN, ROLES.ACCOUNT_MANAGER],
    match: ['/contact-inquiries'],
  },
  {
    label: 'Settings',
    path: '/account/profile',
    icon: <SettingsIcon />,
    permission: PERMISSIONS.SETTINGS_VIEW,
    match: ['/account', '/society-admin', '/platform-admin', '/settings'],
  },
];

const SETTINGS_NAV_ITEMS = [
  {
    label: 'Profile',
    path: '/account/profile',
    permission: PERMISSIONS.SETTINGS_VIEW,
    icon: <ProfileIcon />,
  },
  {
    label: 'Security',
    path: '/account/security',
    permission: PERMISSIONS.SETTINGS_VIEW,
    icon: <SecurityIcon />,
  },
  {
    label: 'Preferences',
    path: '/account/preferences',
    permission: PERMISSIONS.SETTINGS_VIEW,
    icon: <PreferencesIcon />,
  },
  {
    label: 'Notifications',
    path: '/account/notifications',
    permission: PERMISSIONS.SETTINGS_VIEW,
    icon: <NotificationsIcon />,
  },
  {
    label: 'Team & Access',
    path: '/society-admin/team',
    permission: 'society.team.read',
    icon: <PeopleIcon />,
  },
  {
    label: 'Roles',
    path: '/society-admin/roles',
    permission: 'society.roles.read',
    icon: <PolicyIcon />,
  },
  {
    label: 'Operations',
    path: '/society-admin/operations',
    permission: 'society.settings.operations.read',
    icon: <OperationsIcon />,
  },
  {
    label: 'Guard Policy',
    path: '/society-admin/guard-policy',
    permission: 'society.settings.guardPolicy.read',
    icon: <SecurityIcon />,
  },
  {
    label: 'Platform IAM',
    path: '/platform-admin/iam',
    permission: 'platform.iam.admin.read',
    icon: <PeopleIcon />,
  },
  {
    label: 'App Versions',
    path: '/platform-admin/app-versions',
    permission: 'platform.appVersions.read',
    icon: <PolicyIcon />,
  },
  {
    label: 'Operation Blocks',
    path: '/platform-admin/operation-blocks',
    permission: 'platform.operationBlocks.read',
    icon: <OperationsIcon />,
  },
  {
    label: 'Platform Policies',
    path: '/platform-admin/platform-policies',
    permission: 'platform.settings.policy.read',
    icon: <PolicyIcon />,
  },
];

const DashboardLayout = ({ title = 'ParkTek Dashboard', children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const user = useSelector((state) => state.auth.user);
  const role = useSelector((state) => state.auth.role);
  const capabilities = useSelector((state) => state.auth.capabilities);
  const activeSocietyId = useSelector((state) => state.context.activeSocietyId);
  const allowedSocietyIds = useSelector((state) => state.context.allowedSocietyIds);
  const societies = useSelector((state) => state.context.societies);
  const [thirdPartyEnabled, setThirdPartyEnabled] = useState(false);
  const [boomBarrierLogsEnabled, setBoomBarrierLogsEnabled] = useState(false);
  const [visitorAnprLogsEnabled, setVisitorAnprLogsEnabled] = useState(false);
  const [societyOperationsVersion, setSocietyOperationsVersion] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);

  useEffect(() => {
    let mounted = true;

    if (!activeSocietyId) {
      setThirdPartyEnabled(false);
      setBoomBarrierLogsEnabled(false);
      setVisitorAnprLogsEnabled(false);
      return undefined;
    }
    if (
      Array.isArray(allowedSocietyIds) &&
      allowedSocietyIds.length > 0 &&
      !allowedSocietyIds.includes(activeSocietyId)
    ) {
      setThirdPartyEnabled(false);
      setBoomBarrierLogsEnabled(false);
      setVisitorAnprLogsEnabled(false);
      return undefined;
    }

    settingsApi
      .getSocietyOperations({ societyId: activeSocietyId })
      .then((response) => {
        if (!mounted) {
          return;
        }

        const payload = response.data?.data || response.data || {};
        setThirdPartyEnabled(payload.operations?.thirdpartyUserImportEnabled === true);
        setBoomBarrierLogsEnabled(payload.operations?.boomBarrierLogsEnabled === true);
        setVisitorAnprLogsEnabled(payload.operations?.visitorAnprLogsEnabled === true);
      })
      .catch(() => {
        if (mounted) {
          setThirdPartyEnabled(false);
          setBoomBarrierLogsEnabled(false);
          setVisitorAnprLogsEnabled(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [activeSocietyId, allowedSocietyIds, societyOperationsVersion]);

  useEffect(() => {
    const refreshSocietyOperations = (event) => {
      if (!event.detail?.societyId || event.detail.societyId === activeSocietyId) {
        setSocietyOperationsVersion((version) => version + 1);
      }
    };
    window.addEventListener('parktek:society-operations-updated', refreshSocietyOperations);
    return () => window.removeEventListener('parktek:society-operations-updated', refreshSocietyOperations);
  }, [activeSocietyId]);

  const navItems = useMemo(() => {
    const normalizedRole = normalizeRole(role);
    const canSeeThirdPartySection =
      Boolean(activeSocietyId) && thirdPartyEnabled && canAccessThirdPartySection(normalizedRole);
    const hasPermission = (permission) =>
      Array.isArray(permission)
        ? canAny(role, permission, user, capabilities)
        : can(role, permission, user, capabilities);
    return NAV_ITEMS.map((item) => {
      if (item.path !== '/account/profile') {
        return item;
      }

      return {
        ...item,
        match: ['/account', '/society-admin', '/platform-admin', '/settings'],
        children: SETTINGS_NAV_ITEMS.filter(
          (child) =>
            hasPermission(child.permission) && (!child.roles || child.roles.includes(normalizedRole)),
        ),
      };
    }).filter((item) => {
      if (!hasPermission(item.permission)) {
        return false;
      }

      if (item.featureFlag && capabilities?.featureFlags?.[item.featureFlag] === false) {
        return false;
      }

      if (item.path === '/thirdparty' && !canSeeThirdPartySection) {
        return false;
      }
      if (item.path === '/boom-barrier-logs' && !boomBarrierLogsEnabled) {
        return false;
      }
      if (item.path === '/anpr-visitor-logs' && !visitorAnprLogsEnabled) {
        return false;
      }

      if (item.roles?.length) {
        return item.roles.includes(normalizedRole);
      }

      return true;
    });
  }, [
    activeSocietyId,
    boomBarrierLogsEnabled,
    capabilities,
    role,
    thirdPartyEnabled,
    user,
    visitorAnprLogsEnabled,
  ]);

  const handleLogout = () => {
    dispatch(logout());
    dispatch(resetContext());
    dispatch(resetUiState());
    navigate('/login', { replace: true });
  };

  const fixedSocietyName = user?.society?.name || user?.societyName;
  const activeSocietyName = useMemo(
    () => societies?.find((society) => society.id === activeSocietyId)?.name || null,
    [societies, activeSocietyId],
  );

  const pageScope = resolvePageScope({
    role,
    pathname,
    activeSocietyName,
    fixedSocietyName,
  });

  let contextControl = null;
  if (pageScope.showSocietySelector) {
    contextControl = <SocietySelector />;
  } else if (pageScope.showScopeChip) {
    contextControl = <ScopeChip label={pageScope.scopeLabel} />;
  }

  const searchProviders = useMemo(
    () => buildGlobalSearchProviders({ navigate, societyId: activeSocietyId, role }),
    [navigate, activeSocietyId, role],
  );
  const searchContext = useMemo(() => ({ societyId: activeSocietyId }), [activeSocietyId]);

  const handleOpenSearch = () => {
    setRecentSearches(storage.getRecentSearches());
    setSearchOpen(true);
  };

  const handleRecordSearch = (term) => {
    setRecentSearches(storage.pushRecentSearch(term));
  };

  return (
    <>
      <AppShell
        title={title}
        subtitle={`${formatRoleLabel(role)} · ${pageScope.scopeLabel}`}
        contextLabel={!contextControl && fixedSocietyName ? `Society : ${fixedSocietyName}` : null}
        contextControl={contextControl}
        navItems={navItems}
        user={user}
        role={role}
        onLogout={handleLogout}
        onSearch={handleOpenSearch}
        settingsPath={
          can(role, PERMISSIONS.SETTINGS_VIEW, user, capabilities) ? '/account/profile' : undefined
        }
      >
        {children}
      </AppShell>
      <GlobalSearch
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        providers={searchProviders}
        context={searchContext}
        recentSearches={recentSearches}
        onRecordSearch={handleRecordSearch}
      />
    </>
  );
};

export default DashboardLayout;
